import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Box,
  Typography,
  Divider
} from "@mui/material";
import { Settings as SettingsIcon } from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

export interface ColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  minWidth?: number;
  align?: "right" | "left" | "center";
}

interface ColumnConfigDialogProps {
  open: boolean;
  onClose: () => void;
  columns: ColumnConfig[];
  onSave: (columns: ColumnConfig[]) => void;
  title?: string;
}

const ColumnConfigDialog: React.FC<ColumnConfigDialogProps> = ({
                                                                 open,
                                                                 onClose,
                                                                 columns,
                                                                 onSave,
                                                                 title = "Konfiguracja kolumn"
                                                               }) => {
  const [localColumns, setLocalColumns] = useState<ColumnConfig[]>([]);

  useEffect(() => {
    if (open) {
      setLocalColumns([...columns]);
    }
  }, [open, columns]);

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const items = Array.from(localColumns);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setLocalColumns(items);
  };

  const handleVisibilityToggle = (columnId: string) => {
    setLocalColumns(prev =>
        prev.map(col =>
            col.id === columnId ? { ...col, visible: !col.visible } : col
        )
    );
  };

  const handleSave = () => {
    onSave(localColumns);
    onClose();
  };

  const handleReset = () => {
    const resetColumns = columns.map(col => ({ ...col, visible: true }));
    setLocalColumns(resetColumns);
  };

  return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <SettingsIcon color="primary" />
            {title}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Przeciągnij cały prostokąt, aby zmienić kolejność kolumn. Przełącz widoczność klikając checkbox.
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="column-config">
              {(provided) => (
                  <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={{
                        minHeight: 200,
                        display: "flex",
                        flexDirection: "column",
                        gap: 8
                      }}
                  >
                    {localColumns.map((column, index) => (
                        <Draggable
                            key={column.id}
                            draggableId={column.id}
                            index={index}
                            isDragDisabled={column.id === "actions"}
                        >
                          {(provided, snapshot) => (
                              <Box
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps} // <- CAŁY BOX JEST DRAG HANDLE!
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    px: 1,
                                    py: 0,
                                    borderRadius: 1,
                                    background: snapshot.isDragging
                                        ? "rgba(25, 118, 210, 0.13)"
                                        : "#f5f8fa",
                                    boxShadow: snapshot.isDragging
                                        ? "0 2px 12px rgba(0,0,0,0.14)"
                                        : "none",
                                    transition: "background 0.2s, box-shadow 0.2s",
                                    cursor: column.id === "actions" ? "not-allowed" : "grab",
                                    opacity: column.id === "actions" ? 0.4 : 1,
                                    userSelect: "none"
                                  }}
                              >
                                <Typography
                                    sx={{
                                      flex: 1,
                                      fontSize: "0.7rem",
                                      fontWeight: column.id === "actions" ? 600 : 400,
                                      color: "text.primary"
                                    }}
                                >
                                  {column.label}
                                </Typography>
                                <Checkbox
                                    checked={column.visible}
                                    onChange={() => handleVisibilityToggle(column.id)}
                                    disabled={column.id === "actions"}
                                    color="primary"
                                    sx={{
                                      "&.Mui-disabled": { opacity: 0.3 }
                                    }}
                                />
                              </Box>
                          )}
                        </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
              )}
            </Droppable>
          </DragDropContext>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReset} variant="outlined" color="secondary">
            Resetuj
          </Button>
          <Button onClick={onClose} variant="outlined">
            Anuluj
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>
  );
};

export default ColumnConfigDialog;